import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Check, Zap, TrendingUp, Crown, ArrowLeft, Loader2, ShieldCheck, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const detectLang = () => {
  const saved = localStorage.getItem("documatch_language");
  if (saved && ["fr", "es", "en"].includes(saved)) return saved;
  return "fr";
};

const gateLabels = {
  fr: {
    title: "Accès partenaire requis",
    sub: "Cette page tarifaire est réservée aux partenaires validés du programme Sponsor Documatch. Une demande d'accès doit être approuvée manuellement par notre équipe avant de pouvoir consulter les tarifs.",
    request: "Demander l'accès",
    home: "Retour à l'accueil",
    verifying: "Vérification de l'accès..."
  },
  es: {
    title: "Acceso de partner requerido",
    sub: "Esta página de precios está reservada a los partners validados del programa Sponsor de Documatch. La solicitud debe ser aprobada manualmente por nuestro equipo antes de poder consultar las tarifas.",
    request: "Solicitar acceso",
    home: "Volver al inicio",
    verifying: "Verificando el acceso..."
  },
  en: {
    title: "Partner access required",
    sub: "This pricing page is reserved for validated partners of the Documatch Sponsor program. Access must be manually approved by our team before pricing can be viewed.",
    request: "Request access",
    home: "Back to home",
    verifying: "Verifying access..."
  }
};

const labels = {
  fr: {
    title: "Devenez sponsor Documatch",
    subtitle: "Augmentez votre visibilité auprès de décideurs B2B en recherche active de solutions GED",
    month: "/mois",
    min: "Minimum 3 mois",
    cta: "Souscrire maintenant",
    form: { company: "Nom de l'entreprise", email: "Email professionnel" },
    features: "Inclus",
    back: "Retour",
    success: "Paiement confirmé ! Votre visibilité sponsor est activée.",
    cancelled: "Paiement annulé. Vous pouvez réessayer.",
    processing: "Vérification du paiement...",
    productBlock: { label: "Vous achetez", status: "Sponsor actif après paiement", priceLabel: "Total mensuel" },
    trust: { secure: "Paiement sécurisé · Données chiffrées (TLS 1.3)", rgpd: "RGPD", privacy: "Confidentialité", terms: "Conditions" },
    premium: {
      headline: "Finalisez votre abonnement Sponsor",
      sub: "Visibilité premium auprès des décideurs B2B",
      includesTitle: "Ce qui est inclus",
      processTitle: "Comment ça marche",
      processSteps: [
        { title: "Paiement sécurisé", desc: "Validation via Stripe (3D Secure, TLS 1.3, PCI-DSS)" },
        { title: "Activation immédiate", desc: "Votre logo apparaît dans le carrousel sous 24h" },
        { title: "Suivi des leads", desc: "Dashboard en temps réel + rapport hebdomadaire" }
      ],
      reassureTitle: "Sans engagement long",
      reassureItems: ["Annulation à tout moment", "Facturation transparente", "Support dédié sous 24h"],
      ctaPay: "Confirmer et payer avec Stripe",
      ctaBack: "← Modifier",
      poweredBy: "Paiement sécurisé par",
      pciCompliant: "PCI-DSS Compliant · Chiffrement bancaire AES-256"
    }
  },
  es: {
    title: "Conviértase en sponsor de Documatch",
    subtitle: "Aumente su visibilidad ante decisores B2B en búsqueda activa de soluciones DMS",
    month: "/mes",
    min: "Mínimo 3 meses",
    cta: "Suscribirse ahora",
    form: { company: "Nombre de la empresa", email: "Email profesional" },
    features: "Incluido",
    back: "Volver",
    success: "Pago confirmado. Su visibilidad como sponsor está activada.",
    cancelled: "Pago cancelado. Puede intentarlo de nuevo.",
    processing: "Verificando pago...",
    productBlock: { label: "Está comprando", status: "Sponsor activo tras el pago", priceLabel: "Total mensual" },
    trust: { secure: "Pago seguro · Datos cifrados (TLS 1.3)", rgpd: "RGPD", privacy: "Privacidad", terms: "Condiciones" },
    premium: {
      headline: "Finalice su suscripción Sponsor",
      sub: "Visibilidad premium ante decisores B2B",
      includesTitle: "Qué incluye",
      processTitle: "Cómo funciona",
      processSteps: [
        { title: "Pago seguro", desc: "Validación vía Stripe (3D Secure, TLS 1.3, PCI-DSS)" },
        { title: "Activación inmediata", desc: "Su logo aparece en el carrusel en 24h" },
        { title: "Seguimiento de leads", desc: "Panel en tiempo real + informe semanal" }
      ],
      reassureTitle: "Sin compromiso largo",
      reassureItems: ["Cancelación en cualquier momento", "Facturación transparente", "Soporte dedicado en 24h"],
      ctaPay: "Confirmar y pagar con Stripe",
      ctaBack: "← Modificar",
      poweredBy: "Pago seguro por",
      pciCompliant: "PCI-DSS Compliant · Cifrado bancario AES-256"
    }
  },
  en: {
    title: "Become a Documatch Sponsor",
    subtitle: "Increase your visibility with B2B decision-makers actively searching for DMS solutions",
    month: "/month",
    min: "Minimum 3 months",
    cta: "Subscribe now",
    form: { company: "Company name", email: "Professional email" },
    features: "Included",
    back: "Back",
    success: "Payment confirmed! Your sponsor visibility is activated.",
    cancelled: "Payment cancelled. You can try again.",
    processing: "Verifying payment...",
    productBlock: { label: "You are purchasing", status: "Sponsor active after payment", priceLabel: "Monthly total" },
    trust: { secure: "Secure payment · Encrypted data (TLS 1.3)", rgpd: "GDPR", privacy: "Privacy", terms: "Terms" },
    premium: {
      headline: "Finalize your Sponsor subscription",
      sub: "Premium visibility with B2B decision-makers",
      includesTitle: "What's included",
      processTitle: "How it works",
      processSteps: [
        { title: "Secure payment", desc: "Validated via Stripe (3D Secure, TLS 1.3, PCI-DSS)" },
        { title: "Instant activation", desc: "Your logo appears in the carousel within 24h" },
        { title: "Lead tracking", desc: "Real-time dashboard + weekly report" }
      ],
      reassureTitle: "No long-term commitment",
      reassureItems: ["Cancel anytime", "Transparent billing", "Dedicated support within 24h"],
      ctaPay: "Confirm and pay with Stripe",
      ctaBack: "← Modify",
      poweredBy: "Secure payment by",
      pciCompliant: "PCI-DSS Compliant · Bank-grade AES-256 encryption"
    }
  }
};

const plans = [
  {
    id: "signal",
    icon: Zap,
    color: "bg-green-600",
    border: "border-green-200",
    price: "900€",
    features: { fr: ["Logo dans le carousel", "Visibilité de base", "Rapport mensuel"], es: ["Logo en el carrusel", "Visibilidad básica", "Informe mensual"], en: ["Logo in carousel", "Basic visibility", "Monthly report"] }
  },
  {
    id: "growth",
    icon: TrendingUp,
    color: "bg-yellow-500",
    border: "border-yellow-200",
    popular: true,
    price: "1 800€",
    features: { fr: ["Position prioritaire", "Landing page dédiée", "Leads transférés", "Rapport hebdomadaire"], es: ["Posición prioritaria", "Landing page dedicada", "Leads transferidos", "Informe semanal"], en: ["Priority position", "Dedicated landing page", "Lead forwarding", "Weekly report"] }
  },
  {
    id: "category_lead",
    icon: Crown,
    color: "bg-red-600",
    border: "border-red-200",
    price: "3 200€",
    features: { fr: ["Position #1 carousel", "Landing premium", "Tous les leads", "Dashboard temps réel", "Boost IA"], es: ["Posición #1 carrusel", "Landing premium", "Todos los leads", "Dashboard tiempo real", "Boost IA"], en: ["#1 carousel position", "Premium landing", "All leads", "Real-time dashboard", "AI boost"] }
  }
];

export default function SponsorsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [language] = useState(detectLang);
  const lbl = labels[language];
  const gate = gateLabels[language];
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [formData, setFormData] = useState({ company: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [confirmingCheckout, setConfirmingCheckout] = useState(false);
  const [status, setStatus] = useState(null);
  const [accessState, setAccessState] = useState("verifying"); // verifying | granted | denied

  // SEO: prevent indexing of the gated pricing page
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Documatch · Sponsor (privé)";
    let robots = document.querySelector('meta[name="robots"]');
    const created = !robots;
    if (!robots) {
      robots = document.createElement("meta");
      robots.name = "robots";
      document.head.appendChild(robots);
    }
    const prevContent = robots.getAttribute("content");
    robots.setAttribute("content", "noindex,nofollow,noarchive");
    return () => {
      document.title = prevTitle;
      if (created) {
        document.head.removeChild(robots);
      } else if (prevContent !== null) {
        robots.setAttribute("content", prevContent);
      }
    };
  }, []);

  // Token gating — validate against backend
  useEffect(() => {
    const token = searchParams.get("token");
    if (!token) {
      setAccessState("denied");
      return;
    }
    let cancelled = false;
    axios.get(`${API}/sponsor-access/verify`, { params: { token } })
      .then(() => { if (!cancelled) setAccessState("granted"); })
      .catch(() => { if (!cancelled) setAccessState("denied"); });
    return () => { cancelled = true; };
  }, [searchParams]);


  // Check return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const st = searchParams.get("status");
    if (st === "cancelled") {
      setStatus("cancelled");
      return;
    }
    if (sessionId) {
      setStatus("processing");
      pollPaymentStatus(sessionId, 0);
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts) => {
    if (attempts >= 5) { setStatus("success"); return; }
    try {
      const { data } = await axios.get(`${API}/sponsors/checkout/status/${sessionId}`);
      if (data.payment_status === "paid") { setStatus("success"); return; }
      if (data.status === "expired") { setStatus("cancelled"); return; }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch { setStatus("success"); }
  };

  const handleCheckout = async (planId) => {
    if (!formData.company || !formData.email) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/sponsors/checkout`, {
        plan: planId,
        company_name: formData.company,
        email: formData.email,
        origin_url: window.location.origin
      });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      alert("Error creating checkout session");
    } finally { setLoading(false); }
  };

  if (accessState === "verifying") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="sponsor-access-verifying">
        <div className="text-center">
          <Loader2 size={36} className="animate-spin text-blue-600 mx-auto mb-3" />
          <p className="text-sm text-slate-600">{gate.verifying}</p>
        </div>
      </div>
    );
  }

  if (accessState === "denied") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4" data-testid="sponsor-access-denied">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 text-center shadow-2xl"
        >
          <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg shadow-blue-900/30">
            <Lock size={24} className="text-white" />
          </div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-300 mb-2">Documatch Sponsor Program</p>
          <h1 className="text-2xl font-bold text-white mb-3">{gate.title}</h1>
          <p className="text-sm text-slate-300 leading-relaxed mb-7">{gate.sub}</p>
          <div className="flex flex-col gap-2.5">
            <Button
              onClick={() => navigate("/#sponsor-request")}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 font-semibold shadow-lg shadow-blue-900/30"
              data-testid="sponsor-access-request-btn"
            >
              {gate.request}
            </Button>
            <Button
              onClick={() => navigate("/")}
              variant="ghost"
              className="w-full text-slate-300 hover:text-white hover:bg-white/5 py-3"
              data-testid="sponsor-access-home-btn"
            >
              <ArrowLeft size={14} className="mr-2" />{gate.home}
            </Button>
          </div>
          <div className="mt-6 pt-5 border-t border-white/10 flex items-center justify-center gap-2 text-[11px] text-slate-400">
            <ShieldCheck size={12} className="text-green-400" />
            <span>Accès partenaire validé manuellement par Documatch</span>
          </div>
        </motion.div>
      </div>
    );
  }

  if (status === "processing") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center"><Loader2 size={40} className="animate-spin text-blue-600 mx-auto mb-4" /><p className="text-slate-600">{lbl.processing}</p></div>
      </div>
    );
  }

  if (status === "success" || status === "cancelled") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className={`max-w-md w-full rounded-2xl p-8 text-center ${status === "success" ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"} border`}>
          <p className={`text-lg font-semibold ${status === "success" ? "text-green-800" : "text-red-800"}`}>
            {status === "success" ? lbl.success : lbl.cancelled}
          </p>
          <Button onClick={() => navigate("/")} className="mt-6">{lbl.back}</Button>
        </div>
      </div>
    );
  }

  // === Premium Conversion Landing (Step 2: after pack + form selection) ===
  if (confirmingCheckout && selectedPlan) {
    const sp = plans.find(p => p.id === selectedPlan);
    const planTitle = sp.id.replace("_", " ").toUpperCase();
    const planFeatures = sp.features[language] || sp.features.fr;
    const premium = lbl.premium;
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50" data-testid="premium-checkout-landing">
        {/* Header */}
        <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
            <button onClick={() => setConfirmingCheckout(false)} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 text-sm font-medium">
              <ArrowLeft size={16} />
              <span className="font-bold tracking-tight text-slate-900">DOCUMATCH</span>
            </button>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <ShieldCheck size={14} className="text-green-600" />
              <span className="hidden sm:inline">{lbl.trust.secure}</span>
            </div>
          </div>
        </nav>

        <div className="max-w-7xl mx-auto px-4 py-8 md:py-12">
          <div className="grid md:grid-cols-5 gap-8 md:gap-12">
            {/* LEFT - Trust building */}
            <div className="md:col-span-3 space-y-6">
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-xs font-semibold mb-3">
                  <Lock size={12} /> Documatch Sponsor Program
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-slate-900 leading-tight">{premium.headline}</h1>
                <p className="text-slate-600 mt-3 text-base">{premium.sub}</p>
              </motion.div>

              {/* Enterprise trust bar */}
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.04 }} className="grid grid-cols-3 gap-3 bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
                <div className="text-center">
                  <p className="text-2xl font-bold text-slate-900">2.300+</p>
                  <p className="text-[11px] text-slate-500 uppercase tracking-wider">Simulations / mois</p>
                </div>
                <div className="text-center border-x border-slate-100">
                  <p className="text-2xl font-bold text-slate-900">7</p>
                  <p className="text-[11px] text-slate-500 uppercase tracking-wider">Pays EU couverts</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-slate-900">B2B</p>
                  <p className="text-[11px] text-slate-500 uppercase tracking-wider">Décideurs qualifiés</p>
                </div>
              </motion.div>

              {/* Selected Pack Summary */}
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 ${sp.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <sp.icon size={22} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Documatch</p>
                    <h3 className="text-xl font-bold text-slate-900">{planTitle}</h3>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{sp.price}<span className="text-sm text-slate-500 font-normal">{lbl.month}</span></p>
                    <p className="text-xs text-slate-400">{lbl.min}</p>
                  </div>
                </div>
                <div className="mt-5 pt-5 border-t border-slate-100">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">{premium.includesTitle}</p>
                  <ul className="space-y-2.5">
                    {planFeatures.map((f, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700">
                        <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check size={12} className="text-green-700" strokeWidth={3} />
                        </div>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>

              {/* Process */}
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                <h2 className="text-lg font-bold text-slate-900 mb-4">{premium.processTitle}</h2>
                <div className="space-y-3">
                  {premium.processSteps.map((step, i) => (
                    <div key={i} className="flex items-start gap-3 bg-white rounded-xl border border-slate-200 p-4 hover:shadow-sm transition-shadow">
                      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 text-white flex items-center justify-center font-bold text-sm flex-shrink-0 shadow-sm">{i+1}</div>
                      <div>
                        <p className="font-semibold text-slate-900 text-sm">{step.title}</p>
                        <p className="text-sm text-slate-600 mt-0.5">{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Reassurance */}
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <ShieldCheck size={20} className="text-green-700" />
                  <h3 className="font-bold text-green-900">{premium.reassureTitle}</h3>
                </div>
                <ul className="space-y-2">
                  {premium.reassureItems.map((it, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-green-800">
                      <Check size={14} className="flex-shrink-0" strokeWidth={3} />{it}
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>

            {/* RIGHT - Sticky Payment */}
            <div className="md:col-span-2">
              <div className="md:sticky md:top-24 space-y-4">
                <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden">
                  {/* Header */}
                  <div className="bg-gradient-to-br from-blue-600 to-blue-700 px-6 py-5 text-white">
                    <p className="text-xs font-semibold uppercase tracking-wider opacity-80 mb-1">{lbl.productBlock.priceLabel}</p>
                    <p className="text-3xl font-bold">{sp.price}<span className="text-sm opacity-80 font-normal">{lbl.month}</span></p>
                    <p className="text-xs opacity-80 mt-1">{planTitle} · {formData.company}</p>
                  </div>

                  {/* Body */}
                  <div className="px-6 py-5 space-y-4">
                    <div className="text-sm text-slate-600">
                      <p className="font-semibold text-slate-900">{formData.email}</p>
                    </div>

                    <Button
                      onClick={() => handleCheckout(selectedPlan)}
                      disabled={loading}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 font-semibold text-base shadow-lg shadow-blue-200"
                      data-testid="premium-pay-btn"
                    >
                      {loading ? <Loader2 size={18} className="animate-spin" /> : <><Lock size={16} className="mr-2" />{premium.ctaPay}</>}
                    </Button>

                    <button onClick={() => setConfirmingCheckout(false)} className="w-full text-center text-xs text-slate-500 hover:text-slate-700">
                      {premium.ctaBack}
                    </button>
                  </div>

                  {/* Trust footer */}
                  <div className="bg-slate-50 border-t border-slate-200 px-6 py-5">
                    <div className="flex items-center justify-center gap-5 mb-3">
                      <svg viewBox="0 0 64 24" className="h-6" xmlns="http://www.w3.org/2000/svg" aria-label="Visa"><path fill="#1A1F71" d="M27.6 0.4l-3.5 23.2h-5.6L22 0.4h5.6zM50.6 15.4l3-8.2 1.7 8.2h-4.7zm6.3 8.2H62L57.7 0.4h-4.8c-1.1 0-2 0.6-2.4 1.6L42.4 23.6h5.9l1.2-3.3h7.2l0.7 3.3zm-14.6-7.6c0-5.7-7.9-6.1-7.8-8.6 0-0.8 0.7-1.6 2.3-1.8 0.8-0.1 3-0.2 5.4 0.9l1-4.6c-1.3-0.5-3-0.9-5.1-0.9-5.4 0-9.2 2.9-9.2 7-0.1 3 2.7 4.7 4.8 5.7 2.1 1 2.9 1.7 2.9 2.6 0 1.4-1.7 2-3.3 2-2.7 0-4.3-0.7-5.6-1.3l-1 4.7c1.3 0.6 3.7 1.1 6.2 1.2 5.8 0 9.5-2.8 9.5-7.2zM18.6 0.4L9.5 23.6H3.6L-1 5.4c-0.3-1.1-0.5-1.5-1.4-2-1.4-0.7-3.7-1.4-5.7-1.9l0.1-0.6h9.5c1.2 0 2.3 0.8 2.6 2.2l2.4 12.8L13.6 0.4h5z"/></svg>
                      <svg viewBox="0 0 48 30" className="h-7" xmlns="http://www.w3.org/2000/svg" aria-label="Mastercard"><circle cx="18" cy="15" r="12" fill="#EB001B"/><circle cx="30" cy="15" r="12" fill="#F79E1B"/><path fill="#FF5F00" d="M24 5.5a12 12 0 010 19 12 12 0 010-19z"/></svg>
                      <svg viewBox="0 0 60 25" className="h-5" xmlns="http://www.w3.org/2000/svg" aria-label="Stripe"><path fill="#635BFF" d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.48zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.62l.21 1.02c.56-.52 1.59-1.28 3.2-1.28 2.87 0 5.57 2.59 5.57 7.36 0 5.21-2.66 7.62-5.56 7.62zm-.97-11.36c-.95 0-1.54.34-1.97.81l.02 6.12c.4.43.98.78 1.95.78 1.52 0 2.54-1.65 2.54-3.87 0-2.15-1.04-3.84-2.54-3.84zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-4.7L32.37 0v3.36l-4.13.88V.88zm-4.32 9.35v9.79H19.8V5.57h3.7l.12 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.43-3.32.86zm-8.55 4.72c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-2.89.54a4.15 4.15 0 0 1-4.27-4.24l.01-13.17 4.02-.86v3.54h3.14V9.1h-3.14v5.85zm-4.91.7c0 2.97-2.31 4.66-5.73 4.66a11.2 11.2 0 0 1-4.46-.93v-3.93c1.38.75 3.1 1.31 4.46 1.31.92 0 1.58-.25 1.58-1 0-1.96-6.2-1.22-6.2-5.74 0-2.93 2.25-4.68 5.55-4.68 1.11 0 2.22.16 3.34.6v3.95a8.86 8.86 0 0 0-3.34-.86c-.86 0-1.4.25-1.4.88 0 1.85 6.2 1 6.2 5.74z"/></svg>
                    </div>
                    <p className="text-xs text-center text-slate-500 flex items-center justify-center gap-1.5 mb-1">
                      <ShieldCheck size={12} className="text-green-600" /> {lbl.trust.secure}
                    </p>
                    <p className="text-[10px] text-center text-slate-400">{premium.pciCompliant}</p>
                    <div className="flex items-center justify-center gap-3 mt-3 text-xs text-slate-400">
                      <a href="#" className="hover:text-slate-600">{lbl.trust.rgpd}</a>
                      <span>·</span>
                      <a href="#" className="hover:text-slate-600">{lbl.trust.privacy}</a>
                      <span>·</span>
                      <a href="#" className="hover:text-slate-600">{lbl.trust.terms}</a>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="sponsors-page">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 text-sm font-medium" data-testid="sponsors-nav-back">
            <ArrowLeft size={16} />
            <span className="font-bold tracking-tight text-slate-900">DOCUMATCH</span>
          </button>
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <ShieldCheck size={14} className="text-green-600" />
            <span>{lbl.trust.secure}</span>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-16 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{lbl.title}</h1>
            <p className="text-slate-600 max-w-2xl mx-auto">{lbl.subtitle}</p>
          </motion.div>

          {/* Plans */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {plans.map((plan, i) => {
              const Icon = plan.icon;
              const isSelected = selectedPlan === plan.id;
              return (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className={`relative rounded-2xl p-6 border-2 cursor-pointer transition-all ${
                    isSelected ? `${plan.border} shadow-lg ring-2 ring-blue-500` : "border-slate-200 hover:border-slate-300"
                  } ${plan.popular ? "md:-mt-4 md:mb-4" : ""}`}
                  onClick={() => setSelectedPlan(plan.id)}
                  data-testid={`plan-${plan.id}`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-yellow-500 text-white text-xs font-bold px-3 py-1 rounded-full">POPULAR</div>
                  )}
                  <div className={`w-12 h-12 ${plan.color} rounded-xl flex items-center justify-center mb-4`}>
                    <Icon size={24} className="text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-1">{plan.id.replace("_", " ").toUpperCase()}</h3>
                  <p className="text-3xl font-bold text-slate-900 mb-1">{plan.price}<span className="text-sm text-slate-500 font-normal">{lbl.month}</span></p>
                  <p className="text-xs text-slate-400 mb-4">{lbl.min}</p>
                  <p className="text-xs font-semibold text-slate-500 uppercase mb-2">{lbl.features}</p>
                  <ul className="space-y-2">
                    {(plan.features[language] || plan.features.fr).map((f, fi) => (
                      <li key={fi} className="flex items-center gap-2 text-sm text-slate-700">
                        <Check size={14} className="text-green-500 flex-shrink-0" />{f}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>

          {/* Checkout Form */}
          {selectedPlan && (() => {
            const sp = plans.find(p => p.id === selectedPlan);
            const planTitle = sp.id.replace("_", " ").toUpperCase();
            return (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="max-w-md mx-auto bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden" data-testid="sponsor-checkout-form">
                {/* Product block */}
                <div className="bg-gradient-to-br from-slate-50 to-blue-50 px-6 py-5 border-b border-slate-200">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{lbl.productBlock.label}</p>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-bold text-slate-900 text-lg">Documatch · {planTitle}</h3>
                      <p className="text-xs text-green-700 flex items-center gap-1 mt-1">
                        <Check size={12} /> {lbl.productBlock.status}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-500">{lbl.productBlock.priceLabel}</p>
                      <p className="text-2xl font-bold text-slate-900">{sp.price}<span className="text-xs text-slate-500 font-normal">{lbl.month}</span></p>
                    </div>
                  </div>
                </div>

                {/* Form */}
                <div className="px-6 py-5 space-y-3">
                  <Input value={formData.company} onChange={e => setFormData(p => ({...p, company: e.target.value}))} placeholder={lbl.form.company} required data-testid="sponsor-company" />
                  <Input type="email" value={formData.email} onChange={e => setFormData(p => ({...p, email: e.target.value}))} placeholder={lbl.form.email} required data-testid="sponsor-email" />
                  <Button
                    onClick={() => setConfirmingCheckout(true)}
                    disabled={!formData.company || !formData.email}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 font-semibold"
                    data-testid="sponsor-checkout-btn"
                  >
                    <Lock size={14} className="mr-2" />{lbl.cta}
                  </Button>
                </div>

                {/* Trust footer */}
                <div className="bg-slate-50 px-6 py-4 border-t border-slate-200">
                  <div className="flex items-center justify-center gap-5 mb-3">
                    {/* Visa SVG inline */}
                    <svg viewBox="0 0 64 24" className="h-6" xmlns="http://www.w3.org/2000/svg" aria-label="Visa">
                      <path fill="#1A1F71" d="M27.6 0.4l-3.5 23.2h-5.6L22 0.4h5.6zM50.6 15.4l3-8.2 1.7 8.2h-4.7zm6.3 8.2H62L57.7 0.4h-4.8c-1.1 0-2 0.6-2.4 1.6L42.4 23.6h5.9l1.2-3.3h7.2l0.7 3.3zm-14.6-7.6c0-5.7-7.9-6.1-7.8-8.6 0-0.8 0.7-1.6 2.3-1.8 0.8-0.1 3-0.2 5.4 0.9l1-4.6c-1.3-0.5-3-0.9-5.1-0.9-5.4 0-9.2 2.9-9.2 7-0.1 3 2.7 4.7 4.8 5.7 2.1 1 2.9 1.7 2.9 2.6 0 1.4-1.7 2-3.3 2-2.7 0-4.3-0.7-5.6-1.3l-1 4.7c1.3 0.6 3.7 1.1 6.2 1.2 5.8 0 9.5-2.8 9.5-7.2zM18.6 0.4L9.5 23.6H3.6L-1 5.4c-0.3-1.1-0.5-1.5-1.4-2-1.4-0.7-3.7-1.4-5.7-1.9l0.1-0.6h9.5c1.2 0 2.3 0.8 2.6 2.2l2.4 12.8L13.6 0.4h5z"/>
                    </svg>
                    {/* Mastercard SVG inline */}
                    <svg viewBox="0 0 48 30" className="h-7" xmlns="http://www.w3.org/2000/svg" aria-label="Mastercard">
                      <circle cx="18" cy="15" r="12" fill="#EB001B"/>
                      <circle cx="30" cy="15" r="12" fill="#F79E1B"/>
                      <path fill="#FF5F00" d="M24 5.5a12 12 0 010 19 12 12 0 010-19z"/>
                    </svg>
                    {/* Stripe SVG inline */}
                    <svg viewBox="0 0 60 25" className="h-5" xmlns="http://www.w3.org/2000/svg" aria-label="Stripe">
                      <path fill="#635BFF" d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.48zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.62l.21 1.02c.56-.52 1.59-1.28 3.2-1.28 2.87 0 5.57 2.59 5.57 7.36 0 5.21-2.66 7.62-5.56 7.62zm-.97-11.36c-.95 0-1.54.34-1.97.81l.02 6.12c.4.43.98.78 1.95.78 1.52 0 2.54-1.65 2.54-3.87 0-2.15-1.04-3.84-2.54-3.84zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-4.7L32.37 0v3.36l-4.13.88V.88zm-4.32 9.35v9.79H19.8V5.57h3.7l.12 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.43-3.32.86zm-8.55 4.72c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-2.89.54a4.15 4.15 0 0 1-4.27-4.24l.01-13.17 4.02-.86v3.54h3.14V9.1h-3.14v5.85zm-4.91.7c0 2.97-2.31 4.66-5.73 4.66a11.2 11.2 0 0 1-4.46-.93v-3.93c1.38.75 3.1 1.31 4.46 1.31.92 0 1.58-.25 1.58-1 0-1.96-6.2-1.22-6.2-5.74 0-2.93 2.25-4.68 5.55-4.68 1.11 0 2.22.16 3.34.6v3.95a8.86 8.86 0 0 0-3.34-.86c-.86 0-1.4.25-1.4.88 0 1.85 6.2 1 6.2 5.74z"/>
                    </svg>
                  </div>
                  <p className="text-xs text-center text-slate-500 flex items-center justify-center gap-1.5">
                    <ShieldCheck size={12} className="text-green-600" /> {lbl.trust.secure}
                  </p>
                  <div className="flex items-center justify-center gap-3 mt-2 text-xs text-slate-400">
                    <a href="#" className="hover:text-slate-600">{lbl.trust.rgpd}</a>
                    <span>·</span>
                    <a href="#" className="hover:text-slate-600">{lbl.trust.privacy}</a>
                    <span>·</span>
                    <a href="#" className="hover:text-slate-600">{lbl.trust.terms}</a>
                  </div>
                </div>
              </motion.div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
